import { ChangeEvent, useState } from "react";
import { Select, InputStyledDiv, Close, Info, OptionProps } from "components";
import { FilterItem, TableFilter, TableHeading, TableOptions } from "components/Table";
import { useParkingManagementContext } from "pages/ParkingManagement/utils/ParkingManagementContext";
import {
  COMPANIES,
  isMasterAdmin,
  STATUSES,
  transformCompanyToOptions,
  transformMunicipalitiesToOptions,
} from "helpers";
import { SearchableSelect } from "components/forms/SearchableSelect";
import { useGetCompaniesQuery } from "services";
import { useGetMunicipalitiesQuery } from "services";
import { twMerge } from "tailwind-merge";
import { t } from "i18next";

export const ParkingTableHeadings = () => {
  const { user, params, onResetFilters, onSort, onFilterByStatus, onChangeCompany, onChangeMunicipality } =
    useParkingManagementContext();
  const {
    data: companiesData,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useGetCompaniesQuery({
    page: 0,
    pageSize: 100,
  });
  const {
    data: municipalitiesData,
    isLoading: isMunicipalitiesLoading,
    isError: isMunicipalitiesError,
  } = useGetMunicipalitiesQuery();

  const [showInfo, setShowInfo] = useState<boolean>(false);
  const selectedStatus = STATUSES.find((status) => String(status.id) === String(params.status));

  const resetFilters = (
    <div className="absolute -mt-1.5 flex flex-col">
      {showInfo && (
        <InputStyledDiv className="absolute -mt-4 ml-6 w-max bg-primary/5 p-0 text-xs font-thin">
          {t("parkingManagement.clearAllFilters")}
        </InputStyledDiv>
      )}
      <div onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)} className="relative">
        <Info width={7} height={7} className="ml-4" />
      </div>
      <Close className="text-error-text" onClick={onResetFilters} />
    </div>
  );

  const companyOptions = (() => {
    if (user && !isMasterAdmin(user.type.id)) {
      return [
        {
          value: "",
          label: t("parkingManagement.allCompaniesLabel"),
        },
        {
          value: user.company_id || "0",
          label:
            companiesData?.companies?.find((c) => c.company_id === user.company_id)?.name ||
            t("parkingManagement.currentCompanyLabel"),
        },
      ] as OptionProps[];
    }

    const allCompanies = companiesData?.companies ? transformCompanyToOptions(companiesData.companies) : [];

    return [
      {
        value: "",
        label: t("parkingManagement.allCompaniesLabel"),
      },
      ...allCompanies,
    ];
  })();

  const municipalityOptions = transformMunicipalitiesToOptions(municipalitiesData || []);

  const headings: Array<JSX.Element | string> = [
    <>
      {resetFilters}
      <TableHeading onClick={(order) => onSort("unique_number", order)} order={params.order || "DESC"}>
        {t("parkingManagement.uniqueNumberID")}
      </TableHeading>
    </>,
    <TableHeading onClick={(order) => onSort("parking_name", order)}>{t("parkingManagement.name")}</TableHeading>,

    // Company Filter
    isCompaniesLoading ? (
      <div>{t("parkingManagement.loadingCompaniesLabel")}</div>
    ) : isCompaniesError ? (
      <div>{t("parkingManagement.errorLoadingCompaniesLabel")}</div>
    ) : (
      <SearchableSelect
        options={companyOptions}
        placeholder={t("parkingManagement.selectCompanyLabel")}
        onChange={onChangeCompany}
        value={user?.company_id}
        disabled={user && !isMasterAdmin(user.type.id) && !user.company?.name}
      />
    ),

    <TableHeading onClick={(order) => onSort("parking_type", order)}>
      {t("parkingManagement.parkingType")}
    </TableHeading>,

    isMunicipalitiesLoading ? (
      <div>{t("parkingManagement.loadingMunicipalitiesLabel")}</div>
    ) : isMunicipalitiesError ? (
      <div>{t("parkingManagement.errorLoadingMunicipalitiesLabel")}</div>
    ) : (
      <SearchableSelect
        options={[{ label: t("parkingManagement.allMunicipalitiesLabel"), value: "" }, ...municipalityOptions]}
        placeholder={t("parkingManagement.selectMunicipalityLabel")}
        value={params.municipality || ""}
        onChange={onChangeMunicipality}
      />
    ),
    <TableHeading onClick={(order) => onSort("is_configured", order)}>
      {t("parkingManagement.isConfigured")}
    </TableHeading>,
    <TableOptions className="border-0 bg-white">{t("parkingManagement.totalParkingSlots")}</TableOptions>,
    <TableOptions className="border-0 bg-white">{t("parkingManagement.paymentOptions")}</TableOptions>,
    <TableOptions className="border-0 bg-white">{t("parkingManagement.amenities")}</TableOptions>,

    // // Optional: Use a TableFilter for status
    // <TableFilter
    //   onFilter={onFilterByStatus}
    //   values={STATUSES}
    //   label="Status"
    //   selectedFilter={typeof params.status !== "undefined" ? (Number(params.status) as FilterItem) : undefined}
    // />,

    <TableOptions>{t("parkingManagement.options")}</TableOptions>,
  ];

  return (
    <>
      {headings.map((heading, index) => {
        const isLast = index === headings.length - 1;

        return (
          <th
            key={index}
            scope="col"
            className={twMerge(
              "h-[100px] max-h-[100px] w-[200px] min-w-[200px] break-words bg-white py-4 text-sm font-semibold text-primary",
              isLast && "shadow-left sticky right-0 z-10"
            )}
          >
            {heading}
          </th>
        );
      })}
    </>
  );
};
